import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Plug, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/stores/authStore';
import {
  getConnectorCatalogue,
  listConnections,
  startConnect,
  testConnection,
  disconnect,
  setCapabilityProvider,
  clearCapabilityProvider,
  isAuthorizeUrlResult,
  getConnectionStatus,
  NATIVE_PROVIDER_NAME,
  type ConnectorCatalogueEntry,
  type Connection,
  type ConnectionStatusDetail,
} from '@/api/connect';

/**
 * Ticket 137.7 — admin Web UI for the Connect Framework (backend: 137.6).
 *
 * Deliberately schema/authKind-driven: every connector renders from its own
 * `authKind` + `configSchema` + `capabilities` — nothing here special-cases
 * a specific connector (Bitrix included). Adding a new connector to the
 * backend registry needs zero changes to this page.
 */
export function ExternalProvidersAdminPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';
  const ownTenantId = useAuthStore((s) => s.user?.tenantId) ?? '';

  const [tenantId, setTenantId] = useState(ownTenantId);
  const [catalogue, setCatalogue] = useState<ConnectorCatalogueEntry[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Per-connector connect form drafts.
  const [instanceLabel, setInstanceLabel] = useState<Record<string, string>>({});
  const [configDraft, setConfigDraft] = useState<Record<string, Record<string, string>>>({});
  const [secretDraft, setSecretDraft] = useState<Record<string, Record<string, string>>>({});
  const [connecting, setConnecting] = useState<string | null>(null);

  // Per (capability, connectorType) delegation state — no read endpoint
  // exists (137.6 scope), so this reflects only the RESULT of a bind/clear
  // call made in this session (AC3).
  const [bindings, setBindings] = useState<Record<string, { provider: string; enabled: boolean }>>(
    {},
  );
  const [bindingError, setBindingError] = useState<Record<string, string>>({});

  // Ticket 137.8 — per-connectorType observability (health/backlog/last
  // sync/cooldown), keyed by connectorType. A status fetch failure for one
  // connector never blocks the others (Promise.allSettled below), and simply
  // omits that connector's panel rather than surfacing a page-level error.
  const [statusByType, setStatusByType] = useState<Record<string, ConnectionStatusDetail>>({});

  const load = useCallback(async (targetTenant: string) => {
    setLoading(true);
    setError(null);
    try {
      const cat = await getConnectorCatalogue();
      setCatalogue(cat);
      if (targetTenant) {
        const conns = await listConnections(targetTenant);
        setConnections(conns);

        const settled = await Promise.allSettled(
          conns.map((c) => getConnectionStatus(targetTenant, c.connectorType)),
        );
        const nextStatus: Record<string, ConnectionStatusDetail> = {};
        settled.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            nextStatus[conns[i].connectorType] = result.value;
          }
        });
        setStatusByType(nextStatus);
      } else {
        setConnections([]);
        setStatusByType({});
      }
    } catch {
      setError(t('externalProviders.error_load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load(tenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectionByType = useMemo(
    () => new Map(connections.map((c) => [c.connectorType, c])),
    [connections],
  );

  function updateConfigField(type: string, key: string, value: string) {
    setConfigDraft((prev) => ({ ...prev, [type]: { ...prev[type], [key]: value } }));
  }

  function updateSecretField(type: string, key: string, value: string) {
    setSecretDraft((prev) => ({ ...prev, [type]: { ...prev[type], [key]: value } }));
  }

  async function handleConnect(e: FormEvent, connector: ConnectorCatalogueEntry) {
    e.preventDefault();
    if (!tenantId) return;
    setConnecting(connector.type);
    setError(null);
    setNotice(null);
    try {
      const config: Record<string, string> = {};
      const secrets: Record<string, string> = {};
      for (const field of connector.configSchema) {
        const value =
          (field.secret ? secretDraft[connector.type]?.[field.key] : configDraft[connector.type]?.[field.key]) ??
          '';
        if (field.secret) secrets[field.key] = value;
        else config[field.key] = value;
      }
      const result = await startConnect(tenantId, {
        connectorType: connector.type,
        instanceLabel: instanceLabel[connector.type] || connector.displayName,
        config,
        secrets,
      });
      if (isAuthorizeUrlResult(result)) {
        window.location.href = result.authorizeUrl;
        return;
      }
      setNotice(t('externalProviders.connected', { name: connector.displayName }));
      await load(tenantId);
    } catch (err) {
      setError(extractDetail(err) ?? t('externalProviders.error_connect'));
    } finally {
      setConnecting(null);
    }
  }

  async function handleTest(connectorType: string) {
    if (!tenantId) return;
    setError(null);
    setNotice(null);
    try {
      const result = await testConnection(tenantId, connectorType);
      setNotice(
        result.reason
          ? t('externalProviders.test_result_reason', { status: result.status, reason: result.reason })
          : t('externalProviders.test_result', { status: result.status }),
      );
      await load(tenantId);
    } catch (err) {
      setError(extractDetail(err) ?? t('externalProviders.error_test'));
    }
  }

  async function handleDisconnect(connectorType: string) {
    if (!tenantId) return;
    setError(null);
    setNotice(null);
    try {
      await disconnect(tenantId, connectorType);
      setNotice(t('externalProviders.disconnected'));
      await load(tenantId);
    } catch (err) {
      setError(extractDetail(err) ?? t('externalProviders.error_disconnect'));
    }
  }

  async function handleToggleCapability(capability: string, connectorType: string, enable: boolean) {
    if (!tenantId) return;
    const key = `${connectorType}:${capability}`;
    setBindingError((prev) => ({ ...prev, [key]: '' }));
    try {
      const result = enable
        ? await setCapabilityProvider(tenantId, capability, connectorType)
        : await clearCapabilityProvider(tenantId, capability);
      setBindings((prev) => ({
        ...prev,
        [key]: { provider: result.provider, enabled: result.enabled },
      }));
    } catch (err) {
      const detail = isAxiosError(err) && err.response?.status === 409
        ? (err.response?.data as { detail?: string } | undefined)?.detail ??
          t('externalProviders.error_no_active_connection')
        : extractDetail(err) ?? t('externalProviders.error_binding');
      setBindingError((prev) => ({ ...prev, [key]: detail }));
    }
  }

  if (!isAdmin) {
    return <p className="text-sm text-destructive">{t('common.access_denied')}</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Plug className="h-5 w-5 text-primary" />
          {t('externalProviders.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('externalProviders.subtitle')}</p>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1 max-w-xs space-y-1">
          <label className="text-sm font-medium">{t('externalProviders.tenant_id_label')}</label>
          <Input
            data-testid="external-providers-tenant-input"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </div>
        <Button
          data-testid="external-providers-load"
          size="sm"
          variant="outline"
          onClick={() => void load(tenantId)}
          disabled={!tenantId || loading}
        >
          {t('externalProviders.load')}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-300">
          {notice}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {catalogue.map((connector) => {
          const existing = connectionByType.get(connector.type);
          return (
            <Card key={connector.type} data-testid={`connector-card-${connector.type}`} className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{connector.displayName}</h3>
                {existing && <StatusBadge status={existing.status} />}
              </div>

              {existing && (
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">
                      {t('externalProviders.instance_label')}:
                    </span>{' '}
                    <span className="font-medium">{existing.instanceLabel}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {t('externalProviders.connected_at')}:
                    </span>{' '}
                    <code className="text-xs">{existing.connectedAt}</code>
                  </div>
                  {existing.statusReason && (
                    <p className="text-xs text-amber-400">{existing.statusReason}</p>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`connector-test-${connector.type}`}
                      onClick={() => void handleTest(connector.type)}
                    >
                      {t('externalProviders.test')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`connector-disconnect-${connector.type}`}
                      onClick={() => void handleDisconnect(connector.type)}
                    >
                      {t('externalProviders.disconnect')}
                    </Button>
                  </div>
                </div>
              )}

              {existing && <StatusPanel status={statusByType[connector.type]} />}

              {connector.authKind === 'OAuth2AuthCode' ? (
                <div className="pt-1">
                  <Button
                    size="sm"
                    data-testid={`connector-oauth-connect-${connector.type}`}
                    disabled={connecting === connector.type || !tenantId}
                    onClick={(e) => void handleConnect(e, connector)}
                  >
                    {connecting === connector.type
                      ? t('common.saving')
                      : t('externalProviders.oauth_connect', { name: connector.displayName })}
                  </Button>
                </div>
              ) : (
                <form
                  data-testid={`connector-form-${connector.type}`}
                  onSubmit={(e) => void handleConnect(e, connector)}
                  className="space-y-2"
                >
                  <div className="space-y-1">
                    <label className="text-xs font-medium">
                      {t('externalProviders.instance_label')}
                    </label>
                    <Input
                      value={instanceLabel[connector.type] ?? ''}
                      onChange={(e) =>
                        setInstanceLabel((prev) => ({ ...prev, [connector.type]: e.target.value }))
                      }
                      placeholder={connector.displayName}
                    />
                  </div>
                  {connector.configSchema.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <label className="text-xs font-medium">{field.label}</label>
                      <Input
                        type={field.secret ? 'password' : 'text'}
                        required={field.required}
                        autoComplete="off"
                        value={
                          (field.secret
                            ? secretDraft[connector.type]?.[field.key]
                            : configDraft[connector.type]?.[field.key]) ?? ''
                        }
                        onChange={(e) =>
                          field.secret
                            ? updateSecretField(connector.type, field.key, e.target.value)
                            : updateConfigField(connector.type, field.key, e.target.value)
                        }
                      />
                      {field.description && (
                        <p className="text-xs text-muted-foreground">{field.description}</p>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-end pt-1">
                    <Button
                      type="submit"
                      size="sm"
                      data-testid={`connector-submit-${connector.type}`}
                      disabled={connecting === connector.type || !tenantId}
                    >
                      {connecting === connector.type
                        ? t('common.saving')
                        : existing
                          ? t('externalProviders.reconnect')
                          : t('externalProviders.connect')}
                    </Button>
                  </div>
                </form>
              )}

              {connector.capabilities.length > 0 && (
                <div className="space-y-2 border-t border-sidebar-border pt-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('externalProviders.delegation_title')}
                  </p>
                  {connector.capabilities.map((capability) => {
                    const key = `${connector.type}:${capability}`;
                    const binding = bindings[key];
                    const isDelegated = binding?.enabled && binding.provider !== NATIVE_PROVIDER_NAME;
                    return (
                      <div key={capability} className="space-y-0.5">
                        <div className="flex items-center justify-between text-sm">
                          <span>{capability}</span>
                          <label className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {isDelegated
                                ? t('externalProviders.delegated_to', { name: connector.displayName })
                                : t('externalProviders.native')}
                            </span>
                            <input
                              type="checkbox"
                              data-testid={`capability-toggle-${capability}-${connector.type}`}
                              checked={!!isDelegated}
                              disabled={!existing}
                              onChange={(e) =>
                                void handleToggleCapability(capability, connector.type, e.target.checked)
                              }
                            />
                          </label>
                        </div>
                        {bindingError[key] && (
                          <p
                            data-testid={`capability-error-${capability}-${connector.type}`}
                            className="text-xs text-red-400"
                          >
                            {bindingError[key]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {!loading && catalogue.length === 0 && (
        <div className="rounded-xl border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
          {t('externalProviders.empty')}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Connection['status'] }) {
  const { t } = useTranslation();
  if (status === 'CONNECTED') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
        <CheckCircle2 className="h-3 w-3" />
        {t('externalProviders.status_connected')}
      </span>
    );
  }
  if (status === 'DEGRADED') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-200">
        <AlertCircle className="h-3 w-3" />
        {t('externalProviders.status_degraded')}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <XCircle className="h-3 w-3" />
      {t('externalProviders.status_disconnected')}
    </span>
  );
}

/**
 * StatusPanel (ticket 137.8) — health/backlog/last-sync/cooldown readout
 * consuming GET .../connections/{type}/status. Renders nothing while the
 * status hasn't loaded yet (or failed to load) rather than a misleading
 * all-zero placeholder — the parent already shows connection-level status
 * (StatusBadge) regardless.
 */
function StatusPanel({ status }: { status: ConnectionStatusDetail | undefined }) {
  const { t } = useTranslation();
  if (!status) return null;

  return (
    <div
      data-testid={`connector-status-${status.connectorType}`}
      className="space-y-1 rounded-md border border-sidebar-border bg-muted/30 px-3 py-2 text-xs"
    >
      <p className="font-medium uppercase tracking-wide text-muted-foreground">
        {t('externalProviders.status_title')}
      </p>
      <p data-testid={`connector-status-backlog-${status.connectorType}`}>
        {t('externalProviders.status_backlog', { count: status.backlog.pending })}
      </p>
      <div data-testid={`connector-status-lastsync-${status.connectorType}`}>
        {status.lastSync.length === 0 ? (
          <p>{t('externalProviders.status_no_sync')}</p>
        ) : (
          status.lastSync.map((entry) => (
            <p key={entry.syncKey}>
              {t('externalProviders.status_last_sync', {
                key: entry.syncKey,
                when: entry.highWater,
              })}
            </p>
          ))
        )}
      </div>
      <p data-testid={`connector-status-cooldown-${status.connectorType}`}>
        {status.cooldown.active
          ? t('externalProviders.status_cooldown_active', { when: status.cooldown.until ?? '' })
          : t('externalProviders.status_cooldown_inactive')}
      </p>
      {status.fallbackServing && (
        <p
          data-testid={`connector-status-fallback-${status.connectorType}`}
          className="text-amber-400"
        >
          {t('externalProviders.status_fallback_note', {
            capabilities: (status.delegatedCapabilities ?? []).join(', '),
          })}
        </p>
      )}
    </div>
  );
}

function extractDetail(err: unknown): string | null {
  if (isAxiosError(err)) {
    return (err.response?.data as { detail?: string } | undefined)?.detail ?? null;
  }
  return null;
}
